import {
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { Form, Formik } from 'formik';
import { useState } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';

import ScaWarningModal from 'hb-react/shared/components/Payment/components/Stripe/PaymentForm/ScaWarningModal';
import { STRIPE_PAYMENT_TYPES } from 'hb-react/shared/components/Payment/constants';
import { usePaymentContext } from 'hb-react/shared/components/Payment/context/PaymentContext';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';
import * as errorReporting from 'hb-react/shared/utils/errorReporting';

import SplitedCardFields from './SplitedCardFields';
import { Errors } from '../Errors';

export const CreditCardForm = () => {
  const {
    paymentAccountConfig: { external_account_id: stripeAccount, type: gateway },
    paymentEndpoint,
    paymentData,
    billingData,
    amount,
    amountFormatted,
    onSuccess,
    isLoading,
    setLoading,
    cardNetwork,
  } = usePaymentContext();

  const [errors, setErrors] = useState(null);
  const [displayScaWarningModal, setDisplayScaWarningModal] = useState(false);
  const [setupIntent, setSetupIntent] = useState(null);
  const [confirmData, setConfirmData] = useState(null);
  const stripe = useStripe();
  const elements = useElements();
  const { flagEvStripeDirectChargeForAllCC } = useFeatureFlag();

  const confirmPaymentOnServer = ({
    paymentIntent,
    data: scaPayload,
    futurePaymentSave = null,
  }) =>
    axios
      .post(paymentEndpoint, {
        ...scaPayload,
        ...paymentData,
        billing: {
          gateway,
          payment_method: scaPayload.order.billing.payment_method,
          future_payment_save: futurePaymentSave,
          transaction_token: paymentIntent.id,
          card_network: cardNetwork,
          id: paymentIntent.id,
        },
      })
      .then((response) => onSuccess(response.data))
      .catch((error) => {
        setErrors(error?.response?.data?.errors || __('An error has occurred'));
        errorReporting.captureException(error);
      });

  const handleServerPaymentIntentionResponse = async (data) => {
    if (!data.requires_customer_additional_action) {
      setLoading(false);

      return onSuccess(data);
    }

    if (data.setup_intent_client_secret) {
      setLoading(true);
      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: STRIPE_PAYMENT_TYPES.CARD,
        card: elements.getElement(CardNumberElement),
        billing_details: billingData,
      });

      if (error) {
        errorReporting.captureException(error);

        return setErrors(error.message);
      }

      const card = elements.getElement(CardNumberElement);

      stripe
        .confirmCardSetup(
          data.setup_intent_client_secret,
          {
            payment_method: {
              card,
              billing_details: {
                name: paymentMethod.billing_details.name,
                email: paymentMethod.billing_details.email,
              },
            },
          },
          { handleActions: !flagEvStripeDirectChargeForAllCC },
        )
        .then((result) => {
          if (result.error) {
            return setErrors(result.error);
          }

          if (
            flagEvStripeDirectChargeForAllCC &&
            result.setupIntent &&
            result.setupIntent.status === 'requires_action'
          ) {
            setLoading(true);
            setConfirmData(data);
            setSetupIntent(result.setupIntent);
            setDisplayScaWarningModal(true);
          } else {
            return confirmPaymentOnServer({
              paymentIntent: result.setupIntent,
              data,
              futurePaymentSave: !data.no_future_payment_save,
            });
          }
        });
    }

    // Re-instancing of the Stripe object is necessary if the user needs to complete a 3DS challenge.
    const stripeForSca = window.Stripe(
      window.__HB_ENV__.STRIPE_PUBLISHABLE_KEY,
      {
        stripeAccount,
      },
    );

    if (data.payment_intent_client_secret) {
      const { paymentIntent, error } = await stripeForSca.handleCardAction(
        data.payment_intent_client_secret,
      );

      if (error) {
        return setErrors(error.message);
      }

      return confirmPaymentOnServer({
        paymentIntent,
        data,
      });
    }
  };

  const createServerPaymentIntation = async (paymentMethod) => {
    try {
      const { data } = await axios.post(Routes.order_management_orders_path(), {
        ...paymentData,
        billing: {
          transaction_token: paymentMethod.id,
          gateway,
          card_network: cardNetwork,
          payment_method: STRIPE_PAYMENT_TYPES.CARD,
        },
      });

      await handleServerPaymentIntentionResponse(data);
    } catch (e) {
      setErrors(e?.response?.data?.errors || __('An error has occurred'));
      errorReporting.captureException(e);
    }
  };

  const onSubmit = async () => {
    setLoading(true);

    const { paymentMethod, error } = await stripe.createPaymentMethod({
      type: STRIPE_PAYMENT_TYPES.CARD,
      card: elements.getElement(CardNumberElement),
      billing_details: billingData,
    });

    if (error) {
      errorReporting.captureException(error);
      setErrors(error.message);
    }

    if (paymentMethod) {
      await createServerPaymentIntation(paymentMethod);
    }

    setLoading(false);
  };

  const handleContinueClick = () => {
    setDisplayScaWarningModal(false); // Hide the modal

    stripe
      .handleNextAction({ clientSecret: setupIntent.client_secret })
      .then((result) => {
        if (result.error) {
          setLoading(false);
          setErrors(result.error.message);
        } else {
          return confirmPaymentOnServer({
            paymentIntent: result.setupIntent,
            data: confirmData,
            futurePaymentSave: !confirmData.no_future_payment_save,
          });
        }
      });
  };

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={{ cardNumber: '', cardExpiry: '', cardCvc: '' }}
    >
      {({ isValid }) => (
        <Form>
          <Box data-testid="stripe-card-element" flexDirection="column">
            <SplitedCardFields />
            <Box
              justifyContent="space-between"
              alignContent="center"
              width="100%"
              mt={3}
              flexDirection="column"
            >
              {errors && <Errors errors={errors} />}
              <Box ml="auto" pl={3}>
                <Button
                  dataTestId="payment-form-submit"
                  type="submit"
                  isLoading={isLoading}
                  isDisabled={!isValid}
                >
                  {amount > 0
                    ? __('Pay %{amountFormatted}').i__({
                        amountFormatted,
                      })
                    : __('Save')}
                </Button>
              </Box>
            </Box>
          </Box>

          {/* Conditional rendering of the 3DS Authentication Warning Modal */}
          <ScaWarningModal
            displayScaWarningModal={displayScaWarningModal}
            handleContinueClick={handleContinueClick}
            isLoading={false}
          />
        </Form>
      )}
    </Formik>
  );
};
